'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();

import { useInfiniteQuery } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Button } from '@workspace/ui/components/button';
import { Checkbox } from '@workspace/ui/components/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@workspace/ui/components/command';
import { ListItem } from '@workspace/ui/components/custom/listing';
import { Sentinel } from '@workspace/ui/components/custom/sentinel';
import { Popover, PopoverContent, PopoverTrigger } from '@workspace/ui/components/popover';
import { useIntersectionObserver } from '@workspace/ui/hooks';
import { cn } from '@workspace/ui/lib';
import type { ApiResponse } from '@workspace/ui/types';
import { ChevronDown, LoaderCircle, type LucideIcon, X } from 'lucide-react';
import Image from 'next/image';
import * as React from 'react';
import { type ReactNode, useCallback, useEffect, useMemo } from 'react';
import { useDebounce } from 'use-debounce';

type FetchOptions = RequestInit & {
  next?: {
    revalidate?: number | false;
    tags?: string[];
  };
  skipAuth?: boolean;
  cache?: 'force-cache' | 'no-store';
  responseType?: 'json' | 'text' | 'blob' | 'arrayBuffer' | 'formData';
};

export interface ApiComboboxOption {
  value: string | number;
  name: string;
  icon?: LucideIcon;
  description?: string;
  image?: {
    path: string;
    alt: string;
  };
  initials?: string;
}

interface BaseComboboxProps {
  placeholder?: string;
  emptyMessage?: string;
  searchPlaceholder?: string;
  className?: string;
  disabled?: boolean;
  showFilter?: boolean;
  /** When true with `showFilter`, the search input is always rendered. */
  alwaysShowFilter?: boolean;
  clearAllButton?: boolean;
  showListValues?: boolean;
  'aria-invalid'?: boolean;
  readOnly?: boolean;
  onBlur?: () => void;
  url?: string;
  currentPageParam?: string;
  perPageParam?: string;
  searchParam?: string;
  pageSize?: number;
  transformResponse?: (response: ApiResponse<unknown>) => ApiComboboxOption[];
  fetchFunction: (url: string, options: FetchOptions) => Promise<ApiResponse<unknown>>;
  additionalParams?: Record<string, string | number | boolean | string[] | number[]>;
  inModal?: boolean;
  customButton?: ReactNode;
  size?: 'base' | 'sm';
  /** When true, option labels in the dropdown stay on one row and truncate with ellipsis. Default false. */
  truncate?: boolean;
  selectionName?: string;
  /** Initial selected item to display before API data loads (single mode only) */
  initialSelectedItem?: ApiComboboxOption;
  /** Initial selected items to display before API data loads (multiple mode only) */
  initialSelectedItems?: ApiComboboxOption[];
  /** When true, single-select trigger shows option description beside the name (dropdown unchanged). */
  showDescriptionInTrigger?: boolean;
  /** Multiple mode only: pinned option that selects every available item. */
  selectAllOption?: {
    label: string;
    selected: boolean;
    onSelect: () => void;
  };
}

interface SingleComboboxProps extends BaseComboboxProps {
  multiple?: false;
  value: string;
  onChange: (value: string, option?: ApiComboboxOption) => void;
}

interface MultipleComboboxProps extends BaseComboboxProps {
  multiple: true;
  value: string[] | ApiComboboxOption[];
  onChange: (value: string[], options?: ApiComboboxOption[]) => void;
}

type ApiComboboxProps = SingleComboboxProps | MultipleComboboxProps;

export function ApiCombobox({
  value,
  onChange,
  placeholder,
  emptyMessage,
  searchPlaceholder,
  className,
  disabled = false,
  multiple = false,
  showFilter = false,
  alwaysShowFilter = false,
  clearAllButton = false,
  showListValues = true,
  readOnly = false,
  onBlur,
  url,
  currentPageParam = 'current_page',
  perPageParam = 'per_page',
  searchParam = 'search',
  pageSize = 50,
  transformResponse,
  fetchFunction,
  additionalParams,
  inModal = false,
  shouldFilter = true,
  customButton,
  size = 'base',
  truncate = false,
  selectionName,
  initialSelectedItem,
  initialSelectedItems,
  showDescriptionInTrigger = false,
  selectAllOption,
  ...props
}: ApiComboboxProps & { shouldFilter?: boolean }) {
  const tPlaceholder = placeholder || 'Select';

  const commandListRef = React.useRef<HTMLDivElement>(null);
  const [open, setOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500);

  // Serialize additionalParams to a stable string for query key
  const serializedAdditionalParams = useMemo(() => {
    if (!additionalParams) return null;
    const sortedEntries = Object.entries(additionalParams).sort(([a], [b]) => a.localeCompare(b));
    return JSON.stringify(
      sortedEntries.map(([key, value]) => [key, Array.isArray(value) ? [...value].sort() : value])
    );
  }, [additionalParams]);

  // Build query key with dependencies (using debounced search term)
  const queryKey = useMemo(
    () => ['api-combobox', url, debouncedSearchTerm, serializedAdditionalParams],
    [url, debouncedSearchTerm, serializedAdditionalParams]
  );

  // Helper function to build URL search parameters
  const buildSearchParams = useCallback(
    (pageParam: number) => {
      const params = new URLSearchParams();
      params.append(currentPageParam, pageParam.toString());
      params.append(perPageParam, pageSize.toString());

      if (debouncedSearchTerm) {
        params.append(searchParam, debouncedSearchTerm);
      }

      if (additionalParams) {
        Object.entries(additionalParams).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            value.forEach((item) => params.append(`${key}[]`, item.toString()));
          } else {
            params.append(key, value.toString());
          }
        });
      }

      return params;
    },
    [currentPageParam, perPageParam, pageSize, debouncedSearchTerm, searchParam, additionalParams]
  );

  // Helper function to transform API response to options
  const transformResponseToOptions = useCallback(
    (response: ApiResponse<unknown>): ApiComboboxOption[] => {
      if (transformResponse) {
        return transformResponse(response);
      }

      // Default transformation - handle both nested (data.data) and flat (data) structures
      const responseData = response.data;
      const dataArray =
        responseData && typeof responseData === 'object' && 'data' in responseData
          ? (responseData as { data: unknown }).data
          : responseData;
      return Array.isArray(dataArray) ? dataArray : [];
    },
    [transformResponse]
  );

  // Helper function to calculate pagination info (avoid relying on total alone — it is often missing or wrong)
  const calculatePagination = useCallback(
    (response: ApiResponse<unknown>, pageParam: number, fetchedCount: number) => {
      const p = response.pagination;

      if (p?.last_page != null && p.last_page > 0) {
        const currentPage = p.current_page ?? pageParam;
        const hasMoreData = currentPage < p.last_page;
        return {
          total: p.total ?? 0,
          nextPage: hasMoreData ? pageParam + 1 : undefined,
        };
      }

      if (p?.next_page_url) {
        return {
          total: p.total ?? 0,
          nextPage: pageParam + 1,
        };
      }

      // No trusted cursor fields: never use total for hasMore (often missing or wrong). End after a short page.
      const total = p?.total ?? 0;
      if (fetchedCount < pageSize) {
        return { total, nextPage: undefined };
      }
      return { total, nextPage: pageParam + 1 };
    },
    [pageSize]
  );

  // Use infinite query for fetching options
  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam = 1 }) => {
      const params = buildSearchParams(pageParam);
      const response = await fetchFunction(`${url}?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response) {
        throw new Error('No response received from fetch function');
      }

      const options = transformResponseToOptions(response);
      const { total, nextPage } = calculatePagination(response, pageParam, options.length);

      return {
        options,
        nextPage,
        total,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    enabled: open && !!url,
    initialPageParam: 1,
  });

  // Flatten all pages into a single options array
  const options = useMemo(() => {
    const seen = new Set<string>();
    const unique: ApiComboboxOption[] = [];
    for (const option of data?.pages.flatMap((page) => page.options) ?? []) {
      const key = option.value.toString();
      if (seen.has(key)) continue;
      seen.add(key);
      unique.push(option);
    }
    return unique;
  }, [data]);

  const loading = isFetching || isFetchingNextPage;
  const hasMore = hasNextPage ?? false;

  // Virtualizer for rendering only visible items
  const virtualizer = useVirtualizer({
    count: options.length,
    getScrollElement: () => commandListRef.current,
    estimateSize: (index) => {
      const rowHeight = options[index]?.description ? 56 : 48;
      const isLast = index === options.length - 1;
      return isLast ? rowHeight : rowHeight + 20;
    },
    overscan: 5,
  });

  // Recalculate virtualizer when popover opens or options change
  useEffect(() => {
    if (open && commandListRef.current && options.length > 0) {
      // Small delay to ensure DOM is ready and has dimensions
      const timer = setTimeout(() => {
        if (commandListRef.current) {
          virtualizer.measure();
        }
      }, 10);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, options.length, virtualizer]);

  // Use Intersection Observer for infinite scrolling
  const sentinelRef = useIntersectionObserver({
    root: commandListRef,
    rootMargin: '50px',
    threshold: 0.1,
    enabled: open && hasMore && !loading,
    onIntersect: useCallback(
      (isIntersecting: boolean) => {
        if (isIntersecting && hasMore && !loading) {
          fetchNextPage();
        }
      },
      [hasMore, loading, fetchNextPage]
    ),
  });

  // Handle search - updates immediately for UX, debouncing handled by useEffect
  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const optionsMap = useMemo(() => {
    const map = options.reduce(
      (acc, option) => {
        acc[option.value] = option;
        return acc;
      },
      {} as Record<string, ApiComboboxOption>
    );

    // Add initial selected item(s) to the map if not already present
    if (!multiple && initialSelectedItem && !map[initialSelectedItem.value]) {
      map[initialSelectedItem.value] = initialSelectedItem;
    }
    if (multiple && initialSelectedItems) {
      initialSelectedItems.forEach((item) => {
        if (!map[item.value]) {
          map[item.value] = item;
        }
      });
    }

    return map;
  }, [options, multiple, initialSelectedItem, initialSelectedItems]);

  // Handle both string[] and ApiComboboxOption[] for multiple mode
  const selectedValueStrings = useMemo(
    () =>
      multiple
        ? Array.isArray(value)
          ? value.filter((v) => typeof v === 'string').map((v) => v as string)
          : []
        : [],
    [multiple, value]
  );

  const selectedValueObjects = useMemo(
    () =>
      multiple
        ? Array.isArray(value)
          ? value.filter((v) => typeof v === 'object').map((v) => v as ApiComboboxOption)
          : []
        : [],
    [multiple, value]
  );

  const handleSelect = useCallback(
    (currentValue: string) => {
      if (multiple) {
        // Get all current values (both strings and objects)
        const allCurrentValues = [
          ...selectedValueStrings,
          ...selectedValueObjects.map((obj) => obj.value.toString()),
        ];

        const isCurrentlySelected = allCurrentValues.includes(currentValue);
        let newValues: string[];
        let newOptions: ApiComboboxOption[];

        if (isCurrentlySelected) {
          // Remove the value
          newValues = selectedValueStrings.filter((v) => v !== currentValue);
          newOptions = selectedValueObjects.filter((obj) => obj.value.toString() !== currentValue);
        } else {
          // Add the value
          newValues = [...selectedValueStrings, currentValue];
          const option = optionsMap[currentValue];
          newOptions = option ? [...selectedValueObjects, option] : selectedValueObjects;
        }

        (onChange as (value: string[], options: ApiComboboxOption[]) => void)(
          newValues,
          newOptions
        );
      } else {
        const option = optionsMap[currentValue];
        (onChange as (value: string, option?: ApiComboboxOption) => void)(
          currentValue === value ? '' : currentValue,
          currentValue === value ? undefined : option
        );
        setOpen(false);
      }
    },
    [multiple, selectedValueStrings, selectedValueObjects, optionsMap, onChange, value]
  );

  const selectedValue = useMemo(() => (multiple ? '' : (value as string)), [multiple, value]);

  // Helper function to check if an option is selected
  const isOptionSelected = useCallback(
    (optionValue: string | number): boolean => {
      const valueStr = optionValue.toString();
      return (
        selectedValueStrings.includes(valueStr) ||
        selectedValueObjects.some((obj) => obj.value.toString() === valueStr)
      );
    },
    [selectedValueStrings, selectedValueObjects]
  );

  const selectedSingleOption = useMemo(
    () => (!multiple && selectedValue ? optionsMap[selectedValue] : undefined),
    [multiple, selectedValue, optionsMap]
  );

  const emptyDisplayText = readOnly ? 'Not provided' : tPlaceholder;

  const displayValue = useMemo(
    () =>
      multiple && selectAllOption?.selected
        ? selectAllOption.label
        : multiple
          ? selectedValueStrings.length + selectedValueObjects.length > 0
            ? !hasMore &&
              debouncedSearchTerm.trim() === '' &&
              selectedValueStrings.length + selectedValueObjects.length === options.length
              ? 'All selected'
              : selectedValueStrings.length + selectedValueObjects.length === 1
                ? selectedValueObjects.length > 0
                  ? selectedValueObjects[0]?.name || '1 selected'
                  : optionsMap[selectedValueStrings[0] || '']?.name || '1 selected'
                : selectionName
                  ? `${selectionName} (${selectedValueStrings.length + selectedValueObjects.length})`
                  : `${selectedValueStrings.length + selectedValueObjects.length} selected`
            : emptyDisplayText
          : optionsMap[selectedValue]?.name || (selectedValue ? '1 selected' : emptyDisplayText),
    [
      multiple,
      selectAllOption?.label,
      selectAllOption?.selected,
      selectedValueStrings,
      selectedValueObjects,
      options.length,
      optionsMap,
      selectedValue,
      emptyDisplayText,
      hasMore,
      selectionName,
      debouncedSearchTerm,
    ]
  );

  const renderInlineNameWithDescription = useCallback(
    (name: string, description: string, descriptionClassName?: string) => (
      <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden text-left">
        <p className="min-w-0 flex-1 truncate" title={name}>
          {name}
        </p>
        <p
          className={cn(
            'text-grey-700 dark:text-grey-400 shrink-0 text-sm leading-5 whitespace-nowrap',
            descriptionClassName
          )}
          title={description}
        >
          {description}
        </p>
      </div>
    ),
    []
  );

  const renderOptionLabel = useCallback(
    (option: ApiComboboxOption) => {
      if (option.description) {
        if (showDescriptionInTrigger) {
          return renderInlineNameWithDescription(
            option.name,
            option.description,
            'text-xs leading-4'
          );
        }

        return (
          <div className="flex min-w-0 flex-1 flex-col text-left">
            <p className="truncate" title={option.name}>
              {option.name}
            </p>
            <p
              className="text-grey-700 dark:text-grey-400 truncate text-xs leading-4"
              title={option.description}
            >
              {option.description}
            </p>
          </div>
        );
      }

      if (truncate) {
        return (
          <p className="truncate text-left" title={option.name}>
            {option.name}
          </p>
        );
      }

      return <p className="text-left">{option.name}</p>;
    },
    [truncate, showDescriptionInTrigger, renderInlineNameWithDescription]
  );

  const renderTriggerSelectedLabel = useCallback(
    (option: ApiComboboxOption) => {
      if (!showDescriptionInTrigger || !option.description) {
        return (
          <p className="min-w-0 flex-1 truncate text-left" title={option.name}>
            {option.name}
          </p>
        );
      }

      return renderInlineNameWithDescription(option.name, option.description);
    },
    [showDescriptionInTrigger, renderInlineNameWithDescription]
  );

  const renderVisualElement = useCallback((option: ApiComboboxOption) => {
    if (option.image) {
      return (
        <Image
          className="shrink-0"
          src={option.image.path}
          alt={option.image.alt}
          width={24}
          height={24}
        />
      );
    }
    if (option.initials) {
      return (
        <div className="bg-grey-100 dark:bg-grey-900 inline-flex items-center justify-center rounded-lg p-1">
          <div className="flex h-5 w-5 items-center justify-center">
            <p className="text-grey-800 text-xs leading-4 font-medium dark:text-neutral-100">
              {option.initials}
            </p>
          </div>
        </div>
      );
    }
    if (option.icon) {
      return (
        <div className="-ml-2 flex size-12 items-center justify-center rounded-2xl bg-pink-400 dark:bg-pink-400">
          <option.icon className="size-6 text-neutral-100 dark:text-neutral-100" />
        </div>
      );
    }
    return null;
  }, []);

  return (
    <div className="notranslate flex w-full max-w-full min-w-0 flex-col gap-1" translate="no">
      <Popover
        open={readOnly ? false : open}
        onOpenChange={(isOpen: boolean) => {
          setOpen(isOpen);
          if (!isOpen && onBlur) {
            onBlur();
          }
          // Reset search term when opening if no value is selected
          if (
            isOpen &&
            !selectedValue &&
            selectedValueStrings.length + selectedValueObjects.length === 0
          ) {
            setSearchTerm('');
          }
        }}
        modal={inModal}
      >
        <PopoverTrigger asChild>
          <Button
            variant="input"
            size="input"
            role="combobox"
            aria-expanded={open}
            aria-controls="combobox-list"
            aria-label={`Label for the combobox ${displayValue}`}
            disabled={disabled}
            aria-invalid={props['aria-invalid'] || false}
            data-readonly={readOnly ? 'true' : undefined}
            className={cn(
              className,
              'w-full max-w-full min-w-0 shrink',
              readOnly &&
                'text-grey-700 dark:text-grey-500 pointer-events-none cursor-default border-transparent p-0 dark:border-transparent',
              displayValue === emptyDisplayText && 'text-grey-700 dark:text-grey-500',
              displayValue !== emptyDisplayText &&
                !disabled &&
                'text-neutral-200 dark:text-neutral-100',
              'disabled:text-grey-300 dark:disabled:text-grey-900',
              size === 'sm' && 'h-9 rounded-xl px-3 py-2.5 text-sm'
            )}
          >
            <div className="flex min-w-0 flex-1 items-center justify-start gap-2 overflow-hidden">
              {selectedSingleOption ? (
                <>
                  {renderVisualElement(selectedSingleOption)}
                  {renderTriggerSelectedLabel(selectedSingleOption)}
                </>
              ) : (
                <p className="truncate text-left">{displayValue}</p>
              )}
            </div>
            {!readOnly &&
              (!multiple && selectedValue ? (
                <div
                  className="flex h-5 w-5 items-center justify-center"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    (onChange as (value: string, option?: ApiComboboxOption) => void)(
                      '',
                      undefined
                    );
                  }}
                >
                  <X className="group-disabled:text-grey-300 dark:group-disabled:text-grey-900 h-5 w-5 text-xl text-neutral-200 hover:text-red-500 dark:text-neutral-100 dark:hover:text-red-400" />
                </div>
              ) : (
                <ChevronDown className="group-disabled:text-grey-300 dark:group-disabled:text-grey-900 h-5 w-5 text-xl text-neutral-200 group-data-[state=open]:rotate-180 dark:text-neutral-100" />
              ))}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          onWheel={(e) => e.stopPropagation()}
          className="notranslate z-1000 w-[var(--radix-popover-trigger-width)] max-w-[var(--radix-popover-trigger-width)] min-w-[var(--radix-popover-trigger-width)] p-4"
          translate="no"
        >
          <Command shouldFilter={shouldFilter}>
            {showFilter &&
              (alwaysShowFilter ||
                (options.length >= 10 && searchTerm.length === 0) ||
                searchTerm.length > 0) && (
                <CommandInput
                  value={searchTerm}
                  placeholder={searchPlaceholder || 'Search'}
                  onValueChange={handleSearchChange}
                />
              )}
            {multiple && selectAllOption && (
              <>
                <button
                  type="button"
                  className={cn(
                    'mt-2 flex h-10 w-full cursor-pointer items-center justify-between rounded-2xl px-3 py-2',
                    'hover:bg-grey-25 hover:dark:bg-grey-900 dark:text-grey-400 hover:font-medium hover:text-neutral-200',
                    selectAllOption.selected &&
                      'bg-grey-25 dark:bg-grey-900 dark:text-grey-400 font-medium text-neutral-200'
                  )}
                  onClick={selectAllOption.onSelect}
                >
                  <p className="text-sm">{selectAllOption.label}</p>
                  <Checkbox
                    id="api-combobox-select-all"
                    value={selectAllOption.selected ? 'true' : 'false'}
                    className="pointer-events-none"
                  />
                </button>
                <div className="border-grey-300 dark:border-grey-800 my-2 border-b" />
              </>
            )}
            <CommandList
              ref={commandListRef}
              className="max-h-[275px] w-full overflow-y-auto overscroll-y-contain"
            >
              {!hasMore && !loading && options.length === 0 && (
                <CommandEmpty>{emptyMessage || 'No options found'}</CommandEmpty>
              )}
              {options.length > 0 && (
                <CommandGroup className="px-0 py-4">
                  {(() => {
                    const virtualItems = virtualizer.getVirtualItems();
                    const scrollElement = commandListRef.current;
                    const totalSize = virtualizer.getTotalSize();

                    // Use virtualization only when:
                    // 1. Popover is open
                    // 2. Scroll element exists and has dimensions
                    // 3. Virtualizer has calculated items and total size
                    const shouldUseVirtualization =
                      open && scrollElement && virtualItems.length > 0 && totalSize > 0;

                    if (!shouldUseVirtualization) {
                      // Render all items as fallback (only when popover is open)
                      if (!open) return null;
                      return options.map((option, index) => {
                        const isLast = index === options.length - 1;
                        return (
                          <CommandItem
                            key={option.value}
                            value={`${option.value}-${option.name}`}
                            onSelect={() => handleSelect(option.value.toString())}
                            className={cn(
                              'mr-2 flex cursor-pointer justify-between gap-2 rounded-2xl pr-3',
                              option.description ? 'h-auto min-h-12 items-center py-2' : 'h-12',
                              'hover:bg-grey-25 hover:dark:bg-grey-900 dark:text-grey-400 hover:font-medium hover:text-neutral-200',
                              !isLast && 'mb-5',
                              (isOptionSelected(option.value) ||
                                selectedValue === option.value.toString()) &&
                                'bg-grey-25 dark:bg-grey-900 dark:text-grey-400 font-medium text-neutral-200'
                            )}
                          >
                            <div
                              className={cn(
                                'flex items-center gap-3',
                                (truncate || option.description) && 'min-w-0 flex-1 overflow-hidden'
                              )}
                            >
                              {renderVisualElement(option)}
                              {renderOptionLabel(option)}
                            </div>
                            {multiple && (
                              <Checkbox
                                id={option.value.toString()}
                                value={isOptionSelected(option.value) ? 'true' : 'false'}
                                onChange={() => handleSelect(option.value.toString())}
                              />
                            )}
                          </CommandItem>
                        );
                      });
                    }

                    // Render virtualized items
                    return (
                      <div
                        style={{
                          height: `${virtualizer.getTotalSize()}px`,
                          width: '100%',
                          position: 'relative',
                        }}
                      >
                        {virtualItems.map((virtualItem) => {
                          const option = options[virtualItem.index];
                          if (!option) return null;
                          const isLast = virtualItem.index === options.length - 1;
                          return (
                            <div
                              key={virtualItem.key}
                              data-index={virtualItem.index}
                              ref={virtualizer.measureElement}
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                transform: `translateY(${virtualItem.start}px)`,
                              }}
                            >
                              <CommandItem
                                value={`${option.value}-${option.name}`}
                                onSelect={() => handleSelect(option.value.toString())}
                                className={cn(
                                  'mr-2 flex cursor-pointer justify-between gap-2 rounded-2xl pr-3',
                                  option.description ? 'h-auto min-h-12 items-center py-2' : 'h-12',
                                  'hover:bg-grey-25 hover:dark:bg-grey-900 dark:text-grey-400 hover:font-medium hover:text-neutral-200',
                                  !isLast && 'mb-5',
                                  (isOptionSelected(option.value) ||
                                    selectedValue === option.value.toString()) &&
                                    'bg-grey-25 dark:bg-grey-900 dark:text-grey-400 font-medium text-neutral-200'
                                )}
                              >
                                <div
                                  className={cn(
                                    'flex items-center gap-3',
                                    (truncate || option.description) &&
                                      'min-w-0 flex-1 overflow-hidden'
                                  )}
                                >
                                  {renderVisualElement(option)}
                                  {renderOptionLabel(option)}
                                </div>
                                {multiple && (
                                  <Checkbox
                                    id={option.value.toString()}
                                    value={isOptionSelected(option.value) ? 'true' : 'false'}
                                    onChange={() => handleSelect(option.value.toString())}
                                  />
                                )}
                              </CommandItem>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </CommandGroup>
              )}
              {/* Sentinel element for infinite scroll */}
              {hasMore && !loading && <Sentinel ref={sentinelRef} />}
              {loading && (
                <div className="text-grey-500 flex items-center justify-center gap-2 py-4">
                  <p className="text-sm">Loading</p>
                  <LoaderCircle className="animate-spin" />
                </div>
              )}
            </CommandList>
            {clearAllButton && (
              <Button
                variant="destructive"
                className="w-full"
                disabled={
                  multiple
                    ? selectedValueStrings.length + selectedValueObjects.length === 0 &&
                      !selectAllOption?.selected
                    : !selectedValue
                }
                onClick={() => {
                  if (multiple) {
                    (onChange as (value: string[], options: ApiComboboxOption[]) => void)([], []);
                  } else {
                    (onChange as (value: string, option?: ApiComboboxOption) => void)(
                      '',
                      undefined
                    );
                  }
                  // Reset search term - query will automatically refetch
                  setSearchTerm('');
                }}
              >
                {'Clear all'}
              </Button>
            )}
            {customButton && <div className="w-full">{customButton}</div>}
          </Command>
        </PopoverContent>
      </Popover>
      {multiple &&
        selectedValueStrings.length + selectedValueObjects.length > 0 &&
        showListValues && (
          <div
            className="flex h-fit max-h-[228px] flex-col gap-0 overflow-y-scroll pr-2"
            data-slot="combobox-list"
          >
            {[
              ...selectedValueStrings,
              ...selectedValueObjects.map((obj) => obj.value.toString()),
            ].map((value) => {
              const option =
                optionsMap[value] ||
                selectedValueObjects.find((obj) => obj.value.toString() === value);
              return (
                <ListItem
                  key={value}
                  hasPointerEvents={false}
                  title={option?.name || ''}
                  titleDescription={showDescriptionInTrigger ? option?.description : undefined}
                  id={value}
                  icon={option ? renderVisualElement(option) : undefined}
                  actionItems={
                    <Button variant="ghost" onClick={() => handleSelect(value)}>
                      <X />
                    </Button>
                  }
                  onChange={() => handleSelect(value)}
                />
              );
            })}
          </div>
        )}
    </div>
  );
}
